import _ from "lodash";
import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  memo,
  useCallback,
} from "react";
import { compose, graphql } from "lib/apolloClient";
import { connect } from "react-redux";
import { colors, fontStyle, ZOOM_AUTH_URL, redirectUri } from "Constants";
import {
  ENABLE_GOOGLE_DRIVE,
  ENABLE_ONE_DRIVE,
} from "Constants/permissionConstants";
import classes from "./ScheduleEvent.scss";
import InputTextField from "UIComponents/InputTextField";
import I18nHOC from "UIComponents/I18nHOC";
import DateSelector from "UIComponents/DateSelector";
import TimeSelectorV2 from "UIComponents/TimeSelectorV2";
import UILabel from "UIComponents/UILabel";
import UIModal from "UIComponents/UIModal";
import UIButton from "UIComponents/UIButton";
import RadioButtonList from "UIComponents/RadioButtonList";
import FullScreenLoader from "UIComponents/FullScreenLoader";
import OauthPopup from "UIComponents/OauthPopup";
import Checkbox from "UIComponents/Checkbox";
import FilterDropdown from "UIComponents/FilterDropdown";
import withLoader from "UIComponents/GqlWithLoader";
import { FormTextInput } from "UIComponents/InputTextField";
import { FormTextAreaInput } from "UIComponents/TextAreaInput";
import ClassCard from "../ClassCard";
import AddClassModal from "../AddClassModal";
import Stepper from "../Stepper";
import Header from "ClassRoom/components/AddAssignment/Header";
import Footer from "ClassRoom/components/AddAssignment/Footer";
import ActionForSelectModal from "MyCalendar/components/ActionForSelectModal";
import {
  getCourseStudentsFoldersFromCache,
  getMultiCourseStudentsFoldersFromCache,
} from "Course/modules/CourseGraphqlHelpers";
import {
  disableGlobalHeader,
  enableGlobalHeader,
  getUserInfo,
  setToastMsg,
} from "Login/modules/LoginModule";
import {
  getUserZoomAuthorizedStatus,
  generateZoomRefreshToken,
  getUserThirdPartyAuthorizedStatus,
} from "modules/Services";
import { updateSettings } from "modules/ConfigurationThunks";
import {
  createPlatformCalendarEvent,
  editPlatformCalendarEvent,
} from "MyCalendar/modules/MyCalendarModule";
import moment from "moment";
import update from "immutability-helper";
import {
  getCourseStudentsFoldersQuery,
  getMultiCourseStudentsFoldersQuery,
} from "Course/modules/CourseQuery";
import { getCalendarEventDetailsQuery } from "MyCalendar/modules/MyCalendarQuery";
import {
  getCourseWiseUsersDetails,
  checkUserAuthScopes,
  getCalendarEventsViewSettingValue,
  START_PICKER_START_TIME,
  START_PICKER_END_TIME,
} from "MyCalendar/modules/Utils";
import classNames from "classnames";
import { getFormattedCoursesDetailsMemoize } from "ClassRoom/modules/utils";

import ThirdPartyAuth from "AppComponents/ThirdPartyAuth";
import ACLStore from "lib/aclStore";
import { getSettingValue } from "modules/PermissionModule";

import { formatDate } from "Utils";
import {
  getSelectedCurriculumId,
  getAcademicYearEndDate,
  getCurrentAcademicYear,
} from "modules/CommonSelectors";
import { MY_CALENDAR_CONSTANTS } from "MyCalendar/constants/stringConstants";
import { getDateMomentObject } from "Utils/DateTimeHelpers";
import { filterCourseByCalendarMeetingCreatePermission } from "ClassRoom/modules/utils";

const isNewZoomAccountLinkingEnable = () => {
  return ACLStore.can("FeatureFlag:NewZoomAccountLinking");
};

const getWeekOrdinalText = weekNumber => {
  switch (weekNumber) {
    case 1:
      return "first";
    case 2:
      return "second";
    case 3:
      return "third";
    case 4:
      return "fourth";
    case 5:
      return "last";
  }
};

const createMonthlyRecurringOptions = ({ date, weekDay, weekNumber }) => {
  const options = [
    {
      label: `Monthly on day ${date}`,
      value: "by_date",
    },
    {
      label: `Monthly on the ${getWeekOrdinalText(weekNumber)} ${weekDay}`,
      value: "by_week",
    },
  ];
  return options;
};

const weekOfMonth = ({ date }) => {
  let weekInYearIndex = date.week();
  /**
   * Special case for the last week of a year.
   * In US the first week of the year is the week that contains 1st jan. So it is very
   * much possible that the first week overlaps with the last week of the previous year.
   * Eg: if 1st jan 2021 is friday, then 27-31 dec 2020 are considered in the 1st week of 2021.
   * In such cases, we manually calulate the week number wrt its actual year
   */
  if (date.year() !== date.weekYear()) {
    weekInYearIndex = date.clone().subtract(1, "week").week() + 1;
  }
  const startDateOfMonth = moment(date).startOf("month");
  const weekIndex = weekInYearIndex - startDateOfMonth.week() + 1;
  /**
   * Week number of a day doesn't always gurantee that it is its nth occurance in that month.
   * For eg: If a month starts from tuesday, then the first monday that occurs is in week #2 of that month.
   * But it is actually the 1st monday of the month. In such cases, we subtract an offset from the week number to
   * get its occurance number
   */
  const weekOffset = date.day() >= startDateOfMonth.day() ? 0 : -1;
  return weekIndex + weekOffset;
};

const styles = {
  radioButtonOptionStyle: {
    flexBasis: "unset",
    marginRight: "24px",
  },
  radioButtonConStyle: {
    marginBottom: "24px",
  },
  videoOptionLabelStyle: {
    marginBottom: "28px",
  },
  meetingRangeTextContainerStyle: {
    justifyContent: "center",
  },
  repeatTextContainerStyle: {
    justifyContent: "center",
    display: "inline-flex",
    width: "auto",
    marginRight: "18px",
  },
  endsOnTextContainerStyle: {
    justifyContent: "center",
    display: "inline-flex",
    width: "auto",
    marginRight: "18px",
  },
  stepperStyle: {
    marginRight: "24px",
  },
  checkboxContainerStyle: {
    height: "15px",
    width: "15px",
  },
  checkboxStyle: {
    borderRadius: "50%",
  },
  meetingTimeLabelStyle: {
    fontWeight: "600",
  },
  addClassLabelStyle: {
    marginTop: 16,
    width: "max-content",
  },
  checkBoxItemStyle: {
    borderRadius: "50%",
    marginTop: "2px",
  },
  checkBoxLabelStyle: {
    fontWeight: "600",
    fontSize: "1.6rem",
  },
  recurringOptionDropdownStyle: {
    display: "inline-flex",
    marginTop: "-6px",
  },
  timeSelectorDropdownContainerStyle: {
    width: "100%",
  },
  timeSelectorDropdownItemStyle: {
    display: "flex",
    width: "100%",
  },
};

const RECURRENCE_FREQUENCY_TYPE = [
  {
    label: "common:day",
    value: "DAILY",
  },
  {
    label: "common:week",
    value: "WEEKLY",
  },
  {
    label: "common:month",
    value: "MONTHLY",
  },
];

const VIDEO_CALL_OPTIONS = [
  {
    label: "common:zoom",
    value: "ZOOM",
    featurePerm: "Common:AllowZoomMeetingCreation",
  },
  {
    label: "common:microsoft_teams",
    value: "MICROSOFT",
    perm: ENABLE_ONE_DRIVE,
    featurePerm: "Common:AllowMicrosoftTeamsMeetingCreation",
  },
  {
    label: "common:google_meet",
    value: "GOOGLE",
    perm: ENABLE_GOOGLE_DRIVE,
    featurePerm: "Common:AllowGoogleMeetMeetingCreation",
  },
  {
    label: "common:add_meeting_link",
    value: "OTHER",
  },
];

const DAYS = [
  {
    label: "S",
    value: 0,
  },
  {
    label: "M",
    value: 1,
  },
  {
    label: "T",
    value: 2,
  },
  {
    label: "W",
    value: 3,
  },
  {
    label: "T",
    value: 4,
  },
  {
    label: "F",
    value: 5,
  },
  {
    label: "S",
    value: 6,
  },
];

const WEEK_VALUES = {
  Sunday: 0,
  Monday: 1,
  Tuesday: 2,
  Wednesday: 3,
  Thursday: 4,
  Friday: 5,
  Saturday: 6,
};

const DayIcon = React.memo(
  ({ label, value, isSelected, updateDayChange, testId }) => {
    const dayButtonClass = classNames(
      { [classes.dayButton]: true },
      { [classes.selectedDayButton]: isSelected }
    );
    return (
      <div
        className={dayButtonClass}
        onClick={() => updateDayChange(value)}
        data-test-id={testId}
      >
        <p className={classes.dayLabel}>{label}</p>
      </div>
    );
  }
);

DayIcon.displayName = "DayIcon";

const MeetingTypeComponent = ({
  meetingType,
  meetingURL,
  meetingChangesLocal,
  isZoomAuthorized,
  isMicrosoftAuthorized,
  onClickLinkZoom,
  onClickLinkMicrosoft,
  t,
  isGoogleAuthorized,
  updateThirPartyAuthRef,
  onClickLinkGoogle,
  onGoogleSuccessCallBack,
  onZoomSuccessCallBack,
  onMicrosoftSuccessCallBack,
  microsoftTeamError,
  testId,
}) => {
  switch (meetingType) {
    case "OTHER":
      return (
        <InputTextField
          name="url"
          placeholder={t("common:enter_with_label", {
            label: t("common:link"),
          })}
          updateInputField={meetingChangesLocal}
          value={meetingURL}
          isRequired={true}
          testId={`${testId}-meetingLink`}
        />
      );
    case "ZOOM":
      if (isNewZoomAccountLinkingEnable()) {
        return isZoomAuthorized ? (
          <div className={classes.linkedTextCon}>
            {t("common:zoom_link_generate_auto")}
          </div>
        ) : (
          <React.Fragment>
            <ThirdPartyAuth
              serviceType="ZOOM"
              ref={updateThirPartyAuthRef}
              onSuccessCallBack={onZoomSuccessCallBack}
            ></ThirdPartyAuth>
            <UIButton
              color={"blue"}
              type={"hollow"}
              containerStyle={{ marginTop: 16 }}
              onClick={onClickLinkZoom}
              testId={`${testId}-linkZoomAccount`}
            >
              {t("common:link_zoom_account")}
            </UIButton>
          </React.Fragment>
        );
      } else {
        return isZoomAuthorized ? (
          <div className={classes.linkedTextCon}>
            {t("common:zoom_link_generate_auto")}
          </div>
        ) : (
          <UIButton
            color={"blue"}
            type={"hollow"}
            containerStyle={{ marginTop: 16 }}
            onClick={onClickLinkZoom}
            testId={`${testId}-linkZoomAccount`}
          >
            {t("common:link_zoom_account")}
          </UIButton>
        );
      }
    case "MICROSOFT":
      return !_.isEmpty(isMicrosoftAuthorized) ? (
        <div className={classes.linkedTextCon}>
          {t("common:zoom_link_generate_auto")}
        </div>
      ) : (
        <React.Fragment>
          <ThirdPartyAuth
            serviceType="MICROSOFT"
            ref={updateThirPartyAuthRef}
            onSuccessCallBack={onMicrosoftSuccessCallBack}
          ></ThirdPartyAuth>
          <UIButton
            color={"blue"}
            type={"hollow"}
            containerStyle={{ marginTop: 16 }}
            onClick={onClickLinkMicrosoft}
            testId={`${testId}-linkMicrosoftAccount`}
          >
            {microsoftTeamError
              ? t(
                  "calendar:reconnect_your_microsoft_account_to_schedule_a_microsoft_meeting"
                )
              : t("common:link_microsoft_account")}
          </UIButton>
        </React.Fragment>
      );

    case "GOOGLE":
      return isGoogleAuthorized ? (
        <div className={classes.linkedTextCon}>
          {t("common:zoom_link_generate_auto")}
        </div>
      ) : (
        <React.Fragment>
          <ThirdPartyAuth
            serviceType="GOOGLE"
            ref={updateThirPartyAuthRef}
            onSuccessCallBack={onGoogleSuccessCallBack}
          ></ThirdPartyAuth>
          <UIButton
            color={"blue"}
            type={"hollow"}
            containerStyle={{ marginTop: 16 }}
            onClick={onClickLinkGoogle}
            testId={`${testId}-linkGoogleAccount`}
          >
            {t("common:link_google_account")}
          </UIButton>
        </React.Fragment>
      );
    default:
      return null;
  }
};

const MeetingTypeComponentMemo = memo(MeetingTypeComponent);

const ScheduleEvent = React.memo(props => {
  const {
    courseId,
    courseDetails,
    portalType,
    showAddClassButton,
    getUserZoomAuthorizedStatus,
    generateZoomRefreshToken,
    t,
    curriculumProgram,
    onClickDiscard,
    mode,
    createHeader,
    meetingData,
    coursesData,
    selectedTimeSlot,
    createPlatformCalendarEvent,
    editPlatformCalendarEvent,
    endDate,
    oldCourseIds,
    oldUserIds,
    preSelectedCoursesDetails,
    refetchUpcomingEvents,
    getUserThirdPartyAuthorizedStatus,
    userMeetingTypePreference,
    updateSettings,
    userId,
    showCalendarAssignmentByDueDate,
    disableGlobalHeader,
    enableGlobalHeader,
    academicYearEndDate,
    testId,
    userCourses,
  } = props;

  const clonedVideoCallOptions = useMemo(() => {
    const options = [];
    _.forEach(VIDEO_CALL_OPTIONS, item => {
      const isFeatureEnabled =
        !item?.featurePerm || ACLStore.can(item?.featurePerm);
      const isEnable = !item.perm || ACLStore.can(item.perm);
      if (isFeatureEnabled && isEnable) {
        options.push({
          ...item,
          label: t(`${item.label}`),
        });
      }
    });
    return options;
  }, [t]);

  const getInitialMeetingType = () => {
    const availableTypes = _.map(clonedVideoCallOptions, "value");

    // If user has a preference and it's still available, use it
    if (
      !_.isEmpty(userMeetingTypePreference) &&
      _.includes(availableTypes, userMeetingTypePreference)
    ) {
      return userMeetingTypePreference;
    }

    // Otherwise, use the first available option (fallback)
    return _.first(availableTypes) || "OTHER";
  };

  const [meetingObj, setMeetingObj] = useState(
    mode === "create"
      ? {
          id: "",
          label: "",
          description: "",
          url: "",
          type: getInitialMeetingType(),
          authToken: "",
          microsoftAuthToken: "",
          isZoomAuthorized: false,
          scheduleDate: _.get(
            selectedTimeSlot,
            "startDate",
            moment().add(1, "days").format("YYYY-MM-DD")
          ),
          scheduleTime: _.get(selectedTimeSlot, "startTime", "00:00"),
          scheduleEndTime: moment(
            _.get(selectedTimeSlot, "startTime", "00:00"),
            "H:mm"
          )
            .add(30, "minutes")
            .format("HH:mm"),
          isRecurring: false,
          frequency: "WEEKLY",
          recurringInterval: 1,
          recurringWeekDays: [],
          monthRecurringUnit: "by_date",
          monthDays: [],
          setPositions: [],
          scheduledEndDate: moment(endDate).format("YYYY-MM-DD"),
        }
      : meetingData
  );

  useEffect(() => {
    const availableTypes = _.map(clonedVideoCallOptions, "value");
    const currentType = _.get(meetingObj, "type", "");

    if (!_.includes(availableTypes, currentType)) {
      const fallbackType = _.first(availableTypes) || "OTHER";
      meetingChangesLocal({ type: fallbackType });
    }
  }, [meetingObj.type, clonedVideoCallOptions, meetingChangesLocal]);

  const coursesUserCanCreateMeeting = filterCourseByCalendarMeetingCreatePermission(
    { courses: userCourses }
  );
  const canCreateMeetingCourseIds = _.map(
    coursesUserCanCreateMeeting,
    "courseId"
  );
  const defaultSelectedUserCourses = _.filter(
    preSelectedCoursesDetails,
    ({ id }) => _.includes(canCreateMeetingCourseIds, id)
  );

  const canCreateMeetingInCurrentCourse = _.includes(
    canCreateMeetingCourseIds,
    courseId
  );

  const [courses, setCourses] = useState(
    mode === "create"
      ? !_.isEmpty(courseId)
        ? getFormattedCoursesDetailsMemoize({
            // if we are creating inside course calendar
            courses: canCreateMeetingInCurrentCourse ? [courseDetails] : [],
            selectedTimeSlot,
            showCalendarAssignmentByDueDate,
          })
        : getFormattedCoursesDetailsMemoize({
            // if we are creating from master calendar
            courses: defaultSelectedUserCourses,
            selectedTimeSlot,
            showCalendarAssignmentByDueDate,
          })
      : coursesData
  );

  const meetingLabel = _.get(meetingObj, "label", "");
  const description = _.get(meetingObj, "description", "");
  const meetingURL = _.get(meetingObj, "url", "");
  const meetingType = _.get(meetingObj, "type", "");
  const isZoomAuthorized = _.get(meetingObj, "isZoomAuthorized", false);
  const isGoogleAuthorized = _.get(meetingObj, "isGoogleAuthorized", false);
  const isMicrosoftAuthorized = _.get(meetingObj, "microsoftAuthToken", "");
  const scheduleDate = _.get(meetingObj, "scheduleDate", "");
  const scheduleStartTime = _.get(meetingObj, "scheduleTime", "");
  const scheduleEndTime = _.get(meetingObj, "scheduleEndTime", "");

  const recurringMeetSelected = _.get(meetingObj, "isRecurring");
  const recurringInterval = _.get(meetingObj, "recurringInterval");
  const recurringFrequencyType = _.get(meetingObj, "frequency");
  const monthRecurringUnit = _.get(meetingObj, "monthRecurringUnit");
  const scheduledEndDate = _.get(meetingObj, "scheduledEndDate");

  const academicYearEndDateMomentObject = getDateMomentObject(
    academicYearEndDate
  );

  const localLocale = moment(scheduleDate, "YYYY-MM-DD");
  localLocale.locale("en");
  const dateForMonthlyRecurringOption = moment(
    scheduleDate,
    "YYYY-MM-DD"
  ).format("DD");
  const weekDayForMonthlyRecurringOption = localLocale.format("dddd");
  const weekNumberForMonthlyRecurringOption = weekOfMonth({
    date: moment(scheduleDate),
  });

  const recurringWeekDays = !_.isEmpty(meetingObj.recurringWeekDays)
    ? meetingObj.recurringWeekDays
    : recurringFrequencyType === "WEEKLY"
    ? [WEEK_VALUES[weekDayForMonthlyRecurringOption]]
    : [];

  const monthlyRecurringOptions = useMemo(
    () =>
      createMonthlyRecurringOptions({
        date: dateForMonthlyRecurringOption,
        weekDay: weekDayForMonthlyRecurringOption,
        weekNumber: weekNumberForMonthlyRecurringOption,
      }),
    [
      dateForMonthlyRecurringOption,
      weekDayForMonthlyRecurringOption,
      weekNumberForMonthlyRecurringOption,
    ]
  );

  const zoomLinkingEnabled = isNewZoomAccountLinkingEnable();

  const [showAddClassModal, changeShowAddClassModal] = useState(false);
  const [coursesSearchText, changeCoursesSeachText] = useState("");
  const [showFullScreenLoader, toggleFullScreenLoader] = useState(false);
  const [showActionForModal, setShowActionForModal] = useState(false);
  const [isScheduleButtonDisabled, setScheduleButtonDisable] = useState(false);
  const [isFromTimeInvalid, setIsFromTimeInvalid] = useState(false);
  const [isToTimeInvalid, setIsToTimeInvalid] = useState(false);
  const [microsoftTeamError, setMicrosoftTeamError] = useState(false);

  const authPopUpRef = useRef(null);
  const updateThirPartyAuthRef = useRef(null);

  const getThirdPartyServiceStatus = async () => {
    toggleFullScreenLoader(true);
    const promises = [
      getUserThirdPartyAuthorizedStatus({ serviceType: "MICROSOFT" }),
    ];
    if (zoomLinkingEnabled) {
      promises.push(getUserThirdPartyAuthorizedStatus({ serviceType: "ZOOM" }));
    } else {
      promises.push(getZoomAuthStatusLocal());
    }

    promises.push(getUserThirdPartyAuthorizedStatus({ serviceType: "GOOGLE" }));

    let isGoogleAuthorized = false;
    const values = await Promise.all(promises);
    const accessToken = _.get(values, ["2", "accessToken"], null);
    if (!_.isEmpty(accessToken)) {
      isGoogleAuthorized = await checkUserAuthScopes({
        accessToken,
        scopes: ["https://www.googleapis.com/auth/calendar"],
      });
    }
    meetingChangesLocal({
      microsoftAuthToken: _.get(values, "0.accessToken"),
      isZoomAuthorized: values[1],
      isGoogleAuthorized: isGoogleAuthorized,
    });
    toggleFullScreenLoader(false);
  };

  useEffect(() => {
    getThirdPartyServiceStatus();
  }, []);

  useEffect(() => {
    if (isToTimeInvalid || isFromTimeInvalid) {
      setScheduleButtonDisable(true);
    } else {
      setScheduleButtonDisable(false);
    }
  }, [isFromTimeInvalid, isToTimeInvalid]);

  const meetingChangesLocal = useCallback(params => {
    setMeetingObj(prevMeetingObj => ({ ...prevMeetingObj, ...params }));
  }, []);

  const getZoomAuthStatusLocal = async () => {
    const zoomAuthorizedStatus = await getUserZoomAuthorizedStatus();
    return zoomAuthorizedStatus;
  };

  const handleDaySelection = value => {
    if (_.includes(recurringWeekDays, value)) {
      meetingChangesLocal({
        recurringWeekDays: _.filter(recurringWeekDays, day => day != value),
      });
    } else {
      meetingChangesLocal({
        recurringWeekDays: [...recurringWeekDays, value],
      });
    }
  };

  const updatedClassItem = ({ id, params }) => {
    const courseIndex = _.findIndex(courses, { id });
    setCourses(
      update(courses, {
        [courseIndex]: { $set: { ...courses[courseIndex], ...params } },
      })
    );
  };

  const onClickAddClasses = params => {
    setCourses([...courses, ...params]);
    changeShowAddClassModal(false);
  };

  const onClickRemoveClass = id => {
    const removeIndex = _.findIndex(courses, course => course.id === id);
    setCourses(update(courses, { $splice: [[removeIndex, 1]] }));
  };

  const onClickLinkZoom = () => {
    if (zoomLinkingEnabled) {
      if (updateThirPartyAuthRef && updateThirPartyAuthRef.current) {
        const onAuthClick = _.get(
          updateThirPartyAuthRef,
          "current.refs.wrappedInstance.onAuthClick"
        );
        if (typeof onAuthClick === "function") {
          onAuthClick({ forceAuthorized: true });
        }
      }
    } else {
      if (authPopUpRef.current) {
        authPopUpRef.current.createPopup();
      }
    }
  };

  const onClickLinkGoogle = () => {
    if (updateThirPartyAuthRef && updateThirPartyAuthRef.current) {
      const onAuthClick = _.get(
        updateThirPartyAuthRef,
        "current.refs.wrappedInstance.onAuthClick"
      );
      if (typeof onAuthClick === "function") {
        onAuthClick({ forceAuthorized: true });
      }
    }
  };

  const onGoogleSuccessCallBack = () => {
    meetingChangesLocal({ isGoogleAuthorized: true });
  };

  const onZoomSuccessCallBack = () => {
    meetingChangesLocal({ isZoomAuthorized: true });
  };

  const onMicrosoftSuccessCallBack = ({ accessToken }) => {
    meetingChangesLocal({ microsoftAuthToken: accessToken });
  };

  const onClickLinkMicrosoft = () => {
    if (updateThirPartyAuthRef && updateThirPartyAuthRef.current) {
      const onAuthClick = _.get(
        updateThirPartyAuthRef,
        "current.refs.wrappedInstance.onAuthClick"
      );
      if (typeof onAuthClick === "function") {
        onAuthClick({ forceAuthorized: true });
      }
    }
  };

  const onAuthCallBack = async accessCode => {
    toggleFullScreenLoader(true);
    const isSuccess = await generateZoomRefreshToken({
      accessCode,
      redirectUri,
    });
    toggleFullScreenLoader(false);
    if (isSuccess) {
      meetingChangesLocal({ isZoomAuthorized: true });
    }
  };

  const recurringFrequencyTypeOptions = _.map(
    RECURRENCE_FREQUENCY_TYPE,
    item => {
      return {
        ...item,
        label: t(`${item.label}`).toLowerCase(),
      };
    }
  );

  const onStepChange = recurringInterval => {
    meetingChangesLocal({ recurringInterval });
  };

  const onRecurringUnitSelect = ({ frequency }) => {
    meetingChangesLocal({
      frequency: _.first(frequency),
      recurringWeekDays: [],
    });
  };

  const onMonthlyRecurringUnitSelect = ({ monthRecurringUnit }) => {
    if (_.first(monthRecurringUnit) === "by_date") {
      meetingChangesLocal({
        monthDays: [+dateForMonthlyRecurringOption],
        setPositions: [],
        monthRecurringUnit,
      });
    } else {
      meetingChangesLocal({
        setPositions: [weekNumberForMonthlyRecurringOption % 5 || -1],
        monthDays: [],
        recurringWeekDays: [WEEK_VALUES[weekDayForMonthlyRecurringOption]],
        monthRecurringUnit,
      });
    }
  };

  const onMeetingEdit = async option => {
    const isSuccess = await editPlatformCalendarEvent({
      meetingObj,
      courses,
      dateForMonthlyRecurringOption,
      oldCourseIds,
      oldUserIds,
      toggleFullScreenLoader,
      onClickDiscard,
      actionFor: option,
    });
    if (!isSuccess) {
      getThirdPartyServiceStatus();
      onEditModalClose();
    }
  };

  const onStartTimeChange = ({ scheduleTime }) => {
    meetingChangesLocal({
      scheduleTime,
      scheduleEndTime: moment(scheduleTime, "HH:mm")
        .add(30, "minutes")
        .format("HH:mm"),
    });
  };

  const onEditModalClose = () => {
    setShowActionForModal(false);
  };

  const _refetchUpcomingEvents = () => {
    if (refetchUpcomingEvents) {
      refetchUpcomingEvents();
    }
  };

  const onClickDone = async () => {
    const isRecurring = _.get(meetingData, "isRecurring", false);
    if (userMeetingTypePreference != _.get(meetingObj, "type", "")) {
      updateSettings({
        settings: [
          {
            name: "TeacherPortal:schedualMeetingTypePreference",
            value: _.get(meetingObj, "type", ""),
          },
        ],
        showToastMsg: false,
      });
    }
    if (mode === "create") {
      const isSuccess = await createPlatformCalendarEvent({
        meetingObj,
        courses,
        dateForMonthlyRecurringOption,
        toggleFullScreenLoader,
        onClickDiscard,
      });
      if (isSuccess) {
        if (isSuccess === "TD_MICROSOFT_TEAM_API_ERROR") {
          meetingChangesLocal({ microsoftAuthToken: "" });
          setMicrosoftTeamError(true);
        } else {
          _refetchUpcomingEvents();
        }
      } else {
        getThirdPartyServiceStatus();
      }
    } else {
      if (isRecurring) {
        setShowActionForModal(true);
      } else {
        const isSuccess = await editPlatformCalendarEvent({
          meetingObj,
          courses,
          dateForMonthlyRecurringOption,
          oldCourseIds,
          oldUserIds,
          toggleFullScreenLoader,
          onClickDiscard,
          actionFor: "ALL",
        });
        if (isSuccess) {
          _refetchUpcomingEvents();
        } else {
          getThirdPartyServiceStatus();
        }
      }
    }
  };

  const getTimeSelectorByPerm = ({
    value,
    dateVal,
    startTime,
    disableConfirmButton,
    updateInputField,
    name,
  }) => {
    return (
      <TimeSelectorV2
        value={value}
        SelectedTimeformat="HH:mm"
        name={name}
        isValueMomentObj={false}
        updateInputField={updateInputField}
        isDisabled={_.isEmpty(dateVal)}
        startTime={moment(startTime, "HH:mm").add(5, "minutes").format("HH:mm")}
        endTime={START_PICKER_END_TIME}
        dropdownItemStyle={styles.timeSelectorDropdownItemStyle}
        dropdownContainerStyle={styles.timeSelectorDropdownContainerStyle}
        timeComponentStyle={{
          padding: "0px",
        }}
        disableConfirmButton={disableConfirmButton}
      ></TimeSelectorV2>
    );
  };

  return (
    <div className={classes.container}>
      <Header
        onClickBack={onClickDiscard}
        mode={mode}
        createHeader={createHeader}
      ></Header>
      <div className={classes.contentWrapper}>
        <div className={classes.content}>
          <OauthPopup
            url={ZOOM_AUTH_URL}
            onCode={onAuthCallBack}
            ref={authPopUpRef}
            serviceType={"ZOOM"}
          ></OauthPopup>
          <div className={classes.inputField}>
            <FormTextInput
              label={t("common:video_meeting_title")}
              name="label"
              placeholder={t("common:enter_with_label", {
                label: t("common:video_meeting_title"),
              })}
              updateInputField={meetingChangesLocal}
              value={meetingLabel}
              isRequired={true}
              size={"large"}
              testId={`${testId}-videoMeetingTitle`}
            />
          </div>
          <div className={classes.inputField}>
            <FormTextAreaInput
              value={description}
              minRows={2}
              label={t("calendar:video_meeting_desc")}
              updateInputField={meetingChangesLocal}
              name="description"
              placeholder={t("common:enter_with_label", {
                label: t("calendar:video_meeting_desc"),
              })}
              testId={`${testId}-videoMeetingDescription`}
            />
          </div>
          <RadioButtonList
            containerStyle={styles.radioButtonConStyle}
            labelContainerStyle={styles.videoOptionLabelStyle}
            options={clonedVideoCallOptions}
            name={"type"}
            value={meetingType}
            updateInputField={meetingChangesLocal}
            isRequired={true}
            showErrorText={true}
            listItemStyle={styles.radioButtonOptionStyle}
            label={t("calendar:meeting_link")}
          />
          <MeetingTypeComponentMemo
            meetingType={meetingType}
            meetingURL={meetingURL}
            meetingChangesLocal={meetingChangesLocal}
            isZoomAuthorized={isZoomAuthorized}
            isMicrosoftAuthorized={isMicrosoftAuthorized}
            onClickLinkZoom={onClickLinkZoom}
            onClickLinkMicrosoft={onClickLinkMicrosoft}
            isGoogleAuthorized={isGoogleAuthorized}
            updateThirPartyAuthRef={updateThirPartyAuthRef}
            onClickLinkGoogle={onClickLinkGoogle}
            t={t}
            onGoogleSuccessCallBack={onGoogleSuccessCallBack}
            onZoomSuccessCallBack={onZoomSuccessCallBack}
            onMicrosoftSuccessCallBack={onMicrosoftSuccessCallBack}
            microsoftTeamError={microsoftTeamError}
            testId={testId}
          />
          <hr className={classes.separator} />
          <div className={classes.meetingContainer}>
            <UILabel
              labelStyle={styles.meetingTimeLabelStyle}
              label={t("calendar:meeting_time")}
            ></UILabel>
            <div className={classes.horizontalRowContainer}>
              <DateSelector
                updateInputField={meetingChangesLocal}
                value={scheduleDate}
                name={"scheduleDate"}
                isValueMomentObj={false}
                minDate={moment().subtract(1, "day")}
                maxDate={academicYearEndDateMomentObject} // max date should be the last day of the current academic year
                testId={`${testId}-dateSelector`}
              ></DateSelector>
              <UILabel
                labelContainerStyle={styles.meetingRangeTextContainerStyle}
                label={t("calendar:from_date")}
                labelStyle={fontStyle.regular}
              />
              {getTimeSelectorByPerm({
                dateVal: scheduleDate,
                value: scheduleStartTime,
                name: "scheduleTime",
                startTime: START_PICKER_START_TIME,
                disableConfirmButton: setIsFromTimeInvalid,
                updateInputField: onStartTimeChange,
              })}
              <UILabel
                labelContainerStyle={styles.meetingRangeTextContainerStyle}
                label={t("calendar:to_date")}
                labelStyle={fontStyle.regular}
              />
              {getTimeSelectorByPerm({
                dateVal: scheduleDate,
                value: scheduleEndTime,
                name: "scheduleEndTime",
                startTime: moment(scheduleStartTime, "HH:mm")
                  .add(5, "minutes")
                  .format("HH:mm"),
                disableConfirmButton: setIsToTimeInvalid,
                updateInputField: meetingChangesLocal,
              })}
            </div>
          </div>
          <div className={classes.recurringMeeting}>
            <Checkbox
              style={styles.checkboxContainerStyle}
              checkboxStyle={styles.checkboxStyle}
              fill={colors.blue400}
              label={t("calendar:recurring_meeting")}
              onCheckboxClick={() =>
                meetingChangesLocal({ isRecurring: !recurringMeetSelected })
              }
              isChecked={recurringMeetSelected}
              labelStyles={styles.checkBoxLabelStyle}
              checkboxItemStyle={styles.checkBoxItemStyle}
            />
            {recurringMeetSelected ? (
              <div className={classes.recurringItemsContainer}>
                <div className={classes.offestSelection}>
                  <UILabel
                    labelContainerStyle={styles.repeatTextContainerStyle}
                    label={t("calendar:repeat_every")}
                    labelStyle={fontStyle.regular}
                  />
                  <Stepper
                    value={recurringInterval}
                    containerStyle={styles.stepperStyle}
                    onValueChange={onStepChange}
                    testId={`${testId}-recurringIntervalStepper`}
                  />
                  <FilterDropdown
                    name={"frequency"}
                    updateInputField={onRecurringUnitSelect}
                    options={recurringFrequencyTypeOptions}
                    value={recurringFrequencyType}
                    isCheckList={false}
                    showAllOption={false}
                    parentContainerStyle={styles.recurringOptionDropdownStyle}
                    testId={`${testId}-frequencyDropdown`}
                  />
                </div>
                {recurringFrequencyType === "WEEKLY" && (
                  <div className={classes.weekdayRepeat}>
                    <UILabel
                      labelContainerStyle={styles.repeatTextContainerStyle}
                      label={t("calendar:repeat_on")}
                      labelStyle={fontStyle.regular}
                    />
                    <div className={classes.daysContainer}>
                      {_.map(DAYS, day => (
                        <DayIcon
                          label={day.label}
                          value={day.value}
                          updateDayChange={handleDaySelection}
                          key={day.value}
                          isSelected={_.includes(recurringWeekDays, day.value)}
                          testId={`${testId}-dayIcon-${day.value}`}
                        />
                      ))}
                    </div>
                  </div>
                )}
                {recurringFrequencyType === "MONTHLY" && (
                  <FilterDropdown
                    updateInputField={onMonthlyRecurringUnitSelect}
                    options={monthlyRecurringOptions}
                    value={monthRecurringUnit}
                    isCheckList={false}
                    showAllOption={false}
                    name="monthRecurringUnit"
                    parentContainerStyle={{ marginBottom: "32px" }}
                    testId={`${testId}-monthRecurringUnit`}
                  />
                )}
                <div>
                  <UILabel
                    labelContainerStyle={styles.endsOnTextContainerStyle}
                    label={t("calendar:ends_on")}
                    labelStyle={fontStyle.regular}
                  />
                  <div className={classes.endDateContainer}>
                    <DateSelector
                      updateInputField={meetingChangesLocal}
                      value={scheduledEndDate}
                      name={"scheduledEndDate"}
                      isValueMomentObj={false}
                      minDate={moment().subtract(1, "day")}
                      maxDate={academicYearEndDateMomentObject} // max date should be the last day of the current academic year
                      testId={`${testId}-dateSelectorEndRecurrence`}
                    ></DateSelector>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
          <hr className={classes.separator} />
          <div className={classes.coursesContainer}>
            {_.map(courses, course => {
              return (
                <ClassCard
                  course={course}
                  key={course.id}
                  courseId={courseId}
                  onClickRemoveClass={onClickRemoveClass}
                  updateItem={params =>
                    updatedClassItem({ id: course.id, params })
                  }
                  t={t}
                  portalType={portalType}
                  testId={`${testId}-classCard`}
                ></ClassCard>
              );
            })}
          </div>
          {showAddClassButton && (
            <UIButton
              size="sm"
              color={"blue"}
              type={"hollow"}
              containerStyle={styles.addClassLabelStyle}
              onClick={() => changeShowAddClassModal(true)}
              testId={`${testId}-uiComponents-button`}
            >
              {t("common:add_with_label", { label: t("common:class") })}
            </UIButton>
          )}
          {showAddClassModal && (
            <UIModal
              isOpen={showAddClassModal}
              modalContent={classes.addClassModalContainer}
              onRequestClose={() => changeShowAddClassModal(false)}
            >
              <AddClassModal
                isSourceMeetingCreation={true}
                curriculumProgram={curriculumProgram}
                onClickClose={() => changeShowAddClassModal(false)}
                onClickAddClasses={onClickAddClasses}
                coursesSearchText={coursesSearchText}
                changeCoursesSeachText={changeCoursesSeachText}
                courses={courses}
                portalType={portalType}
                subText={t("classRoom:add_class_subText1")}
                testId={`${testId}-addClassModal`}
              />
            </UIModal>
          )}
          {showActionForModal ? (
            <ActionForSelectModal
              t={t}
              onAction={onMeetingEdit}
              onClose={onEditModalClose}
              mode="edit"
              testId={testId}
            />
          ) : null}
          {!!showFullScreenLoader && <FullScreenLoader />}
        </div>
      </div>
      <Footer
        mode={mode}
        onClickCancel={onClickDiscard}
        onClickDone={onClickDone}
        isNextDisabled={true}
        isLast={true}
        currentStep="SCHEDULE_CALL_SETTINGS"
        isButtonDisabled={isScheduleButtonDisabled}
      ></Footer>
    </div>
  );
});

ScheduleEvent.displayName = "ScheduleEvent";

const mapStateToProps = (state, ownProps) => {
  const userInfo = getUserInfo();
  const curriculumProgram = _.get(ownProps.course, "curriculumProgram", {});
  const orgId = state.login.userInfo.org_id;
  const { startDate, endDate } = getCurrentAcademicYear({
    organizationId: orgId,
  });
  const userMeetingTypePreference = getSettingValue({
    name: "TeacherPortal:schedualMeetingTypePreference",
    userId: userInfo.id,
    readFromUserAndOrgLevel: true,
  });
  const currentCurriculumProgramId = getSelectedCurriculumId(state);

  const showCalendarAssignmentByDueDate =
    getCalendarEventsViewSettingValue({
      organizationId: orgId,
      currentCurriculumProgramId,
    }) === MY_CALENDAR_CONSTANTS.DUE_DATE;

  return {
    curriculumProgram,
    startDate,
    endDate,
    isData: true,
    isLoading: false,
    userMeetingTypePreference,
    userId: userInfo.id,
    showCalendarAssignmentByDueDate,
    academicYearEndDate: getAcademicYearEndDate({ organizationId: orgId }),
    organizationId: orgId,
  };
};
const mapActionCreators = {
  getUserZoomAuthorizedStatus,
  generateZoomRefreshToken,
  createPlatformCalendarEvent,
  editPlatformCalendarEvent,
  setToastMsg,
  getUserThirdPartyAuthorizedStatus,
  updateSettings,
  disableGlobalHeader,
  enableGlobalHeader,
};

export default compose(
  I18nHOC({ resource: ["common", "classRoom", "calendar"] }),
  connect(mapStateToProps, mapActionCreators),
  graphql(getCourseStudentsFoldersQuery, {
    skip: ({ courseId }) => !courseId,
    name: "getCourseStudentsFolders",
    options: ({ courseId, portalType }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        id: courseId,
        folderType: "CLASSROOM",
        portalType,
      },
    }),
    props({
      getCourseStudentsFolders,
      ownProps: { courseId, isData, isLoading, steps },
    }) {
      const courseDetails = getCourseStudentsFoldersFromCache({
        id: courseId,
        folderType: "CLASSROOM",
      });
      const hasClassStep = _.find(steps, ["value", "CLASS_SELECTION"]);
      return {
        courseDetails,
        isData: hasClassStep ? true : !_.isEmpty(courseDetails) && isData,
        isLoading:
          getCourseStudentsFolders["networkStatus"] == 1 ||
          getCourseStudentsFolders["networkStatus"] == 2 ||
          isLoading,
      };
    },
  }),
  graphql(getMultiCourseStudentsFoldersQuery, {
    skip: ({ courseId, preSelectedCourseIds }) =>
      courseId || _.isEmpty(preSelectedCourseIds),
    name: "getMultiCourseStudentsFolders",
    options: ({ preSelectedCourseIds, portalType }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        ids: preSelectedCourseIds,
        folderType: "CLASSROOM",
        portalType,
      },
    }),
    props({
      getMultiCourseStudentsFolders,
      ownProps: { preSelectedCourseIds, isData, isLoading, steps },
    }) {
      const courseDetails = getMultiCourseStudentsFoldersFromCache({
        ids: preSelectedCourseIds,
        folderType: "CLASSROOM",
      });
      return {
        preSelectedCoursesDetails: courseDetails,
        isData: !_.isEmpty(courseDetails) && isData,
        isLoading:
          getMultiCourseStudentsFolders["networkStatus"] == 1 ||
          getMultiCourseStudentsFolders["networkStatus"] == 2 ||
          isLoading,
      };
    },
  }),
  graphql(getCalendarEventDetailsQuery, {
    skip: ({ mode, eventId }) => mode != "edit" || !eventId,
    name: "getCalendarEventDetails",
    options: ({ eventId, organizationId }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        id: eventId,
        type: "PLATFORM_CALENDAR_EVENT",
        organizationId,
      },
    }),
    props({
      getCalendarEventDetails,
      ownProps: { isData, isLoading, endDate: academicYearEnd },
    }) {
      const data = _.get(getCalendarEventDetails, "node", {});
      const calendarData = {
        ..._.pick(data, ["id", "startTime", "calendars", "endTime", "item"]),
      };

      const {
        item = {},
        id = "",
        startTime = "",
        endTime = "",
        calendars = [],
      } = calendarData;

      const {
        label = "",
        description = "",
        meeting = {},
        users = [],
        repeatEventSettings = {},
      } = item;

      const type = _.get(meeting, "urls.type", "");
      const url = _.get(meeting, "urls.url", "");

      const courseWiseData = getCourseWiseUsersDetails(
        _.map(calendars, course => _.get(course, "parent", "")),
        [...users]
      );

      const oldCourseIds = _.map(calendars, course =>
        _.get(course, "parent.id", "")
      );
      const oldUserIds = _.map(users, user => user.id);

      const authToken = "";
      const microsoftAuthToken = "";
      const isZoomAuthorized = false;

      const scheduleDate = formatDate({
        date: startTime,
        format: "YYYY-MM-DD",
      });
      const scheduleTime = formatDate({ date: startTime, format: "HH:mm" });
      const scheduleEndTime = formatDate({ date: endTime, format: "HH:mm" });

      const isRecurring = repeatEventSettings ? true : false;
      const {
        frequency,
        endDate,
        interval: recurringInterval,
        monthDays,
        setPositions,
        weekDays: recurringWeekDays,
      } = _.pick(repeatEventSettings || {}, [
        "frequency",
        "endDate",
        "interval",
        "monthDays",
        "setPositions",
        "weekDays",
      ]);
      const monthRecurringUnit = (setPositions || []).length
        ? "by_week"
        : "by_date";
      const scheduledEndDate = moment(+endDate || academicYearEnd).format(
        "YYYY-MM-DD"
      );

      const localLocale = moment(scheduleDate, "YYYY-MM-DD");
      localLocale.locale("en");
      const weekDayForMonthlyRecurringOption = localLocale.format("dddd");

      const meetingData = {
        id: id,
        label,
        description,
        url,
        type,
        authToken,
        microsoftAuthToken,
        isZoomAuthorized,
        scheduleDate,
        scheduleTime,
        scheduleEndTime,
        isRecurring,
        frequency: frequency || "WEEKLY",
        recurringInterval,
        recurringWeekDays: recurringWeekDays || [
          WEEK_VALUES[weekDayForMonthlyRecurringOption],
        ],
        monthRecurringUnit,
        monthDays: monthDays || [],
        setPositions: setPositions || [],
        scheduledEndDate,
      };
      const coursesData = courseWiseData.map(courseData => ({
        ...courseData,
        state: {
          state: "PUBLISHED",
          scheduleDate: startTime,
          scheduleTime: startTime,
        },
      }));
      return {
        isData: !_.isEmpty(data) && isData,
        meetingData,
        coursesData,
        oldCourseIds,
        oldUserIds,
        isLoading:
          getCalendarEventDetails["networkStatus"] == 1 ||
          getCalendarEventDetails["networkStatus"] == 2 ||
          isLoading,
      };
    },
  }),
  withLoader
)(ScheduleEvent);
